import { FC, useCallback } from 'react';
import { DocumentsTooltipContentProps } from './DocumentsTooltipContent.types';
import { Text, Icon, Flex, Separator } from '@/_components';
import { ColorType } from '@/_types/colors';

export const DocumentsTooltipContent: FC<DocumentsTooltipContentProps> = ({
  allCount,
  doneCount,
  documentsNeeded,
  documentsProvidedElsewhere,
  documentsNotApplicable,
  documentsMarkedCompleted,
  hasSplitDocumentsToReview,
}) => {
  const hasDocumentsNeeded: boolean = !!documentsNeeded?.length;
  const hasDocumentsProvidedElsewhere: boolean =
    !!documentsProvidedElsewhere?.length;
  const hasDocumentsNotApplicable: boolean = !!documentsNotApplicable?.length;
  const hasDocumentsMarkedCompleted: boolean =
    !!documentsMarkedCompleted?.length;

  const allProvided: boolean = allCount === doneCount && doneCount !== 0;

  const colorMap: { [key: string]: ColorType } = {
    allProvided: 'success',
    hasDocumentsNeeded: 'critical',
    noDocumentsRequested: 'currentcolor',
  };

  const getInfo = useCallback(() => {
    let text: string;
    let color: ColorType;

    switch (true) {
      case allProvided:
        text = 'All documents provided';
        color = colorMap.allProvided;
        break;
      case hasDocumentsNeeded:
        text = 'Documents needed';
        color = colorMap.hasDocumentsNeeded;
        break;
      default:
        text = 'No documents requested';
        color = colorMap.noDocumentsRequested;
    }
    return { text, color };
  }, [
    allProvided,
    hasDocumentsNeeded,
    colorMap?.hasDocumentsNeeded,
    colorMap?.allProvided,
    colorMap?.noDocumentsRequested,
  ]);

  const { text, color } = getInfo();

  return (
    <Flex isColumn={true} gap={4}>
      <Text type="tiny" fontWeight="bold" color={color}>
        {text}
      </Text>
      {hasDocumentsNeeded && (
        <>
          <Separator isInverted={true} />
          <Flex isColumn={true} gap={2}>
            {documentsNeeded.map((documentName, key) => {
              return (
                <Flex isYCentered={true} gap={2} key={key}>
                  <Icon name="dot" size={16} />
                  <Text type="tiny" color="currentcolor">
                    {documentName}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </>
      )}
      {hasDocumentsProvidedElsewhere && (
        <>
          <Text type="tiny" fontWeight="bold" color="critical">
            Documents provided elsewhere
          </Text>
          <Separator isInverted={true}></Separator>
          <Flex isColumn={true} gap={2}>
            {documentsProvidedElsewhere?.map((documentName, key) => {
              return (
                <Flex isYCentered={true} gap={2} key={key}>
                  <Icon name="dot" size={16} />
                  <Text type="tiny" color="currentcolor">
                    {documentName}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </>
      )}
      {hasDocumentsNotApplicable && (
        <>
          <Text type="tiny" fontWeight="bold" color="critical">
            Documents not applicable
          </Text>
          <Separator isInverted={true}></Separator>
          <Flex isColumn={true} gap={2}>
            {documentsNotApplicable?.map((documentName, key) => {
              return (
                <Flex isYCentered={true} gap={2} key={key}>
                  <Icon name="dot" size={16} />
                  <Text type="tiny" color="currentcolor">
                    {documentName}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </>
      )}
      {hasDocumentsMarkedCompleted && (
        <>
          <Text type="tiny" fontWeight="bold" color="critical">
            Documents marked completed
          </Text>
          <Separator isInverted={true}></Separator>
          <Flex isColumn={true} gap={2}>
            {documentsMarkedCompleted?.map((documentName, key) => {
              return (
                <Flex isYCentered={true} gap={2} key={key}>
                  <Icon name="dot" size={16} />
                  <Text type="tiny" color="currentcolor">
                    {documentName}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </>
      )}
      {hasSplitDocumentsToReview && (
        <>
          <Separator isInverted={true} />
          <Text type="tiny" fontWeight="bold" color="success">
            Split documents ready to review
          </Text>
        </>
      )}
    </Flex>
  );
};
